import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { I18nProvider } from "./lib/i18n";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Shell from "./components/Shell";
import { Loading } from "./components/common";

import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import UseCases from "./pages/UseCases";
import UseCaseDetail from "./pages/UseCaseDetail";
import Regulatory from "./pages/Regulatory";
import RedTeam from "./pages/RedTeam";
import Voice from "./pages/Voice";
import Simulator from "./pages/Simulator";
import AutoFix from "./pages/AutoFix";
import Users from "./pages/Users";
import Lifecycle from "./pages/Lifecycle";
import KPI from "./pages/KPI";
import Value from "./pages/Value";
import Integrations from "./pages/Integrations";
import Admin from "./pages/Admin";
import Gate from "./pages/Gate";
import PortalUserManager from "./pages/PortalUserManager";
import { ModulePage } from "./pages/ModulePage";

function Protected({ children }) {
  const { user, l1, ready } = useAuth();
  if (!ready) return <Loading />;
  if (!l1) return <Navigate to="/emergent-admin-login" replace />;
  if (!user) return <Navigate to="/application-login" replace />;
  return <Shell>{children}</Shell>;
}

function AdminOnly({ children }) {
  const { isAdmin, ready } = useAuth();
  if (!ready) return <Loading />;
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/emergent-admin-login" element={<Gate />} />
            <Route path="/application-login" element={<Login />} />
            <Route path="/login" element={<Navigate to="/application-login" replace />} />
            <Route path="/gate" element={<Navigate to="/emergent-admin-login" replace />} />
            <Route path="/portal-manager" element={<Protected><AdminOnly><PortalUserManager /></AdminOnly></Protected>} />
            <Route path="/" element={<Protected><Dashboard /></Protected>} />
            <Route path="/portfolio" element={<Protected><ModulePage moduleKey="portfolio" /></Protected>} />
            <Route path="/usecases" element={<Protected><UseCases /></Protected>} />
            <Route path="/usecases/:id" element={<Protected><UseCaseDetail /></Protected>} />
            <Route path="/lifecycle" element={<Protected><Lifecycle /></Protected>} />
            <Route path="/regulatory" element={<Protected><Regulatory /></Protected>} />
            <Route path="/risk" element={<Protected><ModulePage moduleKey="risk" /></Protected>} />
            <Route path="/data" element={<Protected><ModulePage moduleKey="data" /></Protected>} />
            <Route path="/security" element={<Protected><ModulePage moduleKey="security" /></Protected>} />
            <Route path="/redteam" element={<Protected><RedTeam /></Protected>} />
            <Route path="/models" element={<Protected><ModulePage moduleKey="models" /></Protected>} />
            <Route path="/agents" element={<Protected><ModulePage moduleKey="agents" /></Protected>} />
            <Route path="/rag" element={<Protected><ModulePage moduleKey="rag" /></Protected>} />
            <Route path="/policies" element={<Protected><ModulePage moduleKey="policies" /></Protected>} />
            <Route path="/controls" element={<Protected><ModulePage moduleKey="controls" /></Protected>} />
            <Route path="/compliance" element={<Protected><ModulePage moduleKey="compliance" /></Protected>} />
            <Route path="/audit" element={<Protected><ModulePage moduleKey="audit" /></Protected>} />
            <Route path="/monitoring" element={<Protected><ModulePage moduleKey="monitoring" /></Protected>} />
            <Route path="/kpi" element={<Protected><KPI /></Protected>} />
            <Route path="/value" element={<Protected><Value /></Protected>} />
            <Route path="/voice" element={<Protected><Voice /></Protected>} />
            <Route path="/simulator" element={<Protected><Simulator /></Protected>} />
            <Route path="/autofix" element={<Protected><AutoFix /></Protected>} />
            <Route path="/integrations" element={<Protected><Integrations /></Protected>} />
            <Route path="/users" element={<Protected><AdminOnly><Users /></AdminOnly></Protected>} />
            <Route path="/admin" element={<Protected><AdminOnly><Admin /></AdminOnly></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
